'use client'

import React from 'react'

import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, children, ...props }, ref) => {
  return (
    <span
      data-slot="select-value"
      className={twMerge(
        'pointer-events-none [&_*]:pointer-events-none flex-1 min-w-0 truncate group-data-[placeholder]:block group-data-[placeholder]:w-full group-data-[placeholder]:text-left',
        className,
      )}
    >
      <SelectPrimitive.Value ref={ref} {...props}>
        {children}
      </SelectPrimitive.Value>
    </span>
  )
})

SelectValue.displayName = SelectPrimitive.Value.displayName

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    noIcon?: boolean
  }
>(({ className, children, noIcon = false, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={twMerge(
      'group flex h-9 w-full items-center justify-between text-left gap-x-2 rounded-md transition-colors duration-100 border border-primary-200 hover:border-primary-300 dark:border-primary-700 dark:hover:border-primary-600 bg-inherit pl-3 pr-2 py-2 text-sm placeholder:text-primary-500 dark:placeholder:text-primary-400 placeholder:select-none data-[placeholder]:select-none dark:data-[placeholder]:text-primary-400 disabled:cursor-not-allowed disabled:opacity-50',
      'text-primary-900 dark:text-primary-100 data-[placeholder]:text-primary-500 dark:data-[placeholder]:text-primary-400',
      className,
    )}
    type="button"
    {...props}
  >
    {children}
    {!noIcon && (
      <SelectPrimitive.Icon asChild={true}>
        <ChevronDown className="flex-shrink-0 h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    )}
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    viewportClassName?: string
  }
>(
  (
    { className, children, position = 'popper', viewportClassName, ...props },
    ref,
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={twMerge(
          'relative focus:ring-0 z-50 min-w-32 max-h-72 overflow-y-auto rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-primary-900 text-primary-900 dark:text-primary-100 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={twMerge(
            'p-1 space-y-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
            viewportClassName,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={twMerge('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={twMerge(
        'relative flex items-center justify-between gap-x-2 w-full text-primary-900 dark:text-primary-100 rounded-[4px] cursor-default select-none py-1.5 px-2 text-sm outline-none hover:bg-primary-200 dark:hover:bg-primary-800 focus:bg-primary-200 dark:focus:bg-primary-800 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex-1">
        {children}
      </SelectPrimitive.ItemText>
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={twMerge(
      '-mx-1 my-1 h-px bg-primary-300 dark:bg-primary-700',
      className,
    )}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

const SelectEmpty = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge(
      'px-2 py-[5px] text-sm text-primary-500 dark:text-primary-400',
      className,
    )}
    {...props}
  />
))
SelectEmpty.displayName = 'SelectEmpty'

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectEmpty,
}
